import { useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router";

import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  type ChatMessage,
  createMessageComponents,
  extractLessonKeys,
  CUSTOM_PATH_DRAFT_PREFIX,
} from "sj-ui-toolkit/src/atlas/components/AspenChat";

import S from "./ChatMessageList.module.scss";

// Stable module-level reference — prevents ReactMarkdown from remounting renderers.
const messageComponents = createMessageComponents((href, children) => (
  <Link to={href.replace(/\\/g, "")} className={S.link}>
    {children}
  </Link>
));

interface ChatMessageListProps {
  messages: ChatMessage[];
  transcriptLoading: boolean;
  error: string | null;
  streaming: boolean;
}

export function ChatMessageList({
  messages,
  transcriptLoading,
  error,
  streaming,
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const lastAssistantMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === "assistant") return messages[i];
    }
    return null;
  }, [messages]);

  const lessonKeysInLastMessage = useMemo(
    () => (lastAssistantMessage ? extractLessonKeys(lastAssistantMessage.content) : []),
    [lastAssistantMessage]
  );

  const handleCreateCustomPath = useCallback(() => {
    if (!lessonKeysInLastMessage.length) return;
    const uuid = crypto.randomUUID();
    sessionStorage.setItem(
      `${CUSTOM_PATH_DRAFT_PREFIX}${uuid}`,
      JSON.stringify(lessonKeysInLastMessage)
    );
    window.location.assign(
      `${AdminAppRoutes.routes.paths.routes.new.route}?customPathDraft=${uuid}`
    );
  }, [lessonKeysInLastMessage]);

  return (
    <>
      {transcriptLoading && (
        <div className={S.transcriptLoading} role="status" aria-live="polite" aria-atomic="true">
          <ProgressSpinner style={{ width: "18px", height: "18px" }} strokeWidth="5" />
          Loading chat session...
        </div>
      )}

      <div
        className={C(S.messageLog, themeClasses.scrollbar)}
        role="log"
        aria-live="polite"
        aria-label="Conversation"
      >
        <div className={S.messageList}>
          {messages.map((msg: ChatMessage) => (
            <div
              key={msg.id}
              className={C(S.messageRow, msg.role === "user" ? S.user : S.assistant)}
            >
              {msg.role === "assistant" && (
                <div className={S.assistantAvatar} aria-hidden="true">
                  <i className="pi pi-sparkles text-surface-950" style={{ fontSize: "0.65rem" }} />
                </div>
              )}
              <div className={C(S.messageBubble, msg.role === "user" ? S.user : S.assistant)}>
                {msg.role === "user" ? (
                  msg.content
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={messageComponents}>
                    {msg.content}
                  </ReactMarkdown>
                )}
                {msg.streaming && !msg.content && (
                  <span className={S.typingIndicator} aria-hidden="true">
                    <span className={S.typingDot} />
                    <span className={S.typingDot} />
                    <span className={S.typingDot} />
                  </span>
                )}
                {msg.streaming && msg.content && (
                  <span className={S.streamingCursor} aria-hidden="true" />
                )}
              </div>
            </div>
          ))}
        </div>

        {error && <Message severity="error" text={error} className="w-full mt-4" />}

        {!streaming && !transcriptLoading && lessonKeysInLastMessage.length > 0 && (
          <div className={S.customPathContainer}>
            <Button
              label={`Create Custom Path from ${lessonKeysInLastMessage.length} lesson${lessonKeysInLastMessage.length === 1 ? "" : "s"}`}
              icon="pi pi-sitemap"
              onClick={handleCreateCustomPath}
              aria-label={`Create a custom path from the ${lessonKeysInLastMessage.length} lesson${lessonKeysInLastMessage.length === 1 ? "" : "s"} listed above`}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </>
  );
}
